import { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, managersApi } from '../../../lib/api.js';
import { useAreas } from '../../../hooks/useAreas.js';
import { Button } from '../../../components/ui/Button.js';
import { Select } from '../../../components/ui/Select.js';
import { useToast } from '../../../components/ui/Toast.js';

export const Route = createFileRoute('/_auth/admin/managers')({
  component: ManagersPage,
});

function ManagersPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const { data: areas } = useAreas();

  const managers = (usersData?.items ?? []).filter(
    (u) => ['manager', 'admin'].includes(u.role) && u.active
  );

  const { data: managerAreas, refetch } = useQuery({
    queryKey: ['manager-areas', selectedManagerId],
    queryFn: () => managersApi.getAreas(selectedManagerId!),
    enabled: selectedManagerId !== null,
  });

  const [selectedAreaIds, setSelectedAreaIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (managerAreas) {
      setSelectedAreaIds(new Set(managerAreas.areas.map((a) => a.id)));
    }
  }, [managerAreas]);

  const saveMutation = useMutation({
    mutationFn: () =>
      managersApi.setAreas(selectedManagerId!, Array.from(selectedAreaIds)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-areas', selectedManagerId] });
      toast('Areas saved', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-xl font-bold text-primary-text mb-4">Manager areas</h1>

      <div className="bg-white border border-border rounded-xl p-4 space-y-4">
        <Select
          label="Select manager"
          placeholder="Choose a manager…"
          options={managers.map((m) => ({ value: m.id, label: `${m.name} (${m.role})` }))}
          value={selectedManagerId ?? ''}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null;
            setSelectedManagerId(id);
          }}
        />

        {selectedManagerId !== null && areas && (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-primary-text">
                  Areas ({selectedAreaIds.size} selected)
                </p>
                <button
                  onClick={() => {
                    if (areas && selectedAreaIds.size === areas.length) {
                      setSelectedAreaIds(new Set());
                    } else if (areas) {
                      setSelectedAreaIds(new Set(areas.map((a) => a.id)));
                    }
                  }}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  {areas && selectedAreaIds.size === areas.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="border border-border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                {areas.map((area) => (
                  <label
                    key={area.id}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-hover-bg cursor-pointer border-b border-border last:border-0 min-h-[44px]"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAreaIds.has(area.id)}
                      onChange={(e) => {
                        setSelectedAreaIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(area.id);
                          else next.delete(area.id);
                          return next;
                        });
                      }}
                      className="w-4 h-4 rounded border-border text-primary"
                    />
                    <span className="text-sm text-primary-text">{area.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button
              className="w-full"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save areas
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
